import type { TemporalValue } from '#layers/base/public';
import type { PrimitiveTypeAnyDateTime } from '#layers/data-model/types/schema/primitive-type';
import { Temporal } from 'temporal-polyfill';
import {
	PRIMITIVE_TYPE_DATE,
	PRIMITIVE_TYPE_DATE_TIME,
	PRIMITIVE_TYPE_DATE_TIME_WITH_TIMEZONE,
	PRIMITIVE_TYPE_TIME,
} from '#layers/data-model/constants/schema/primitive-type';
import { resolveFieldParser } from './primitive-field-parsers';

/**
 * Backend primitive type names whose parser serializes a Temporal operand.
 */
type DateTimeTypeName = PrimitiveTypeAnyDateTime['name'];

/**
 * Serializes a canonical Temporal value to its backend wire string via the field-parser contract.
 *
 * Temporal values expose no enumerable own properties, so callers that stringify them structurally
 * (JSON, object-literal serializers) would otherwise emit `{}`. Falls back to `toString()` for
 * Temporal kinds without a date-like field parser (e.g. `Temporal.Instant`).
 */
export function serializeTemporalValue(value: TemporalValue): string {
	const typeName = temporalTypeName(value);
	const serialized = typeName ? resolveFieldParser(typeName)?.serialize?.(value as never) : undefined;

	return typeof serialized === 'string' ? serialized : value.toString();
}

/**
 * Maps a Temporal operand to the date-like primitive type whose parser produces its wire string.
 */
function temporalTypeName(value: TemporalValue): DateTimeTypeName | undefined {
	if (value instanceof Temporal.PlainDate) return PRIMITIVE_TYPE_DATE;
	if (value instanceof Temporal.PlainTime) return PRIMITIVE_TYPE_TIME;
	if (value instanceof Temporal.PlainDateTime) return PRIMITIVE_TYPE_DATE_TIME;
	if (value instanceof Temporal.ZonedDateTime) return PRIMITIVE_TYPE_DATE_TIME_WITH_TIMEZONE;
}
